import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import { Plus, Pencil, Trash2, X, Check, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import MultiImageUpload from '@/components/MultiImageUpload';
import { resolveImgUrl } from '@/utils/imgUrl';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const empty = { nama: '', deskripsi: '', harga: '', fotos: [], kategori: 'Buku', whatsapp: '', stok: '', is_active: true };

export default function AdminToko() {
  const [produk, setProduk] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { fetch(); }, []);
  const fetch = () => axios.get(`${API}/produk/admin/all`).then(r => setProduk(r.data)).catch(() => {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      harga: parseInt(form.harga),
      stok: form.stok ? parseInt(form.stok) : null,
      foto: form.fotos[0] || '',  // backward compat for public toko
    };
    try {
      if (editId) { await axios.put(`${API}/produk/${editId}`, payload); toast.success('Produk diperbarui'); }
      else { await axios.post(`${API}/produk`, payload); toast.success('Produk ditambahkan'); }
      setShowForm(false); setEditId(null); setForm(empty); fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus produk ini?')) return;
    await axios.delete(`${API}/produk/${id}`); toast.success('Produk dihapus'); fetch();
  };

  const rupiah = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6 flex items-start md:items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-emerald-950">Toko ZahBaq</h1>
            <p className="text-sm text-gray-500">{produk.length} produk tersedia</p>
          </div>
          <button data-testid="add-produk-btn" onClick={() => { setForm(empty); setEditId(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-emerald transition-all">
            <Plus size={16} /> Tambah Produk
          </button>
        </div>
        <div className="p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {produk.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`bg-white rounded-2xl overflow-hidden shadow-card border border-gray-50 ${!p.is_active ? 'opacity-60' : ''}`}>
              {p.fotos?.[0] || p.foto ? (
                <img src={resolveImgUrl(p.fotos?.[0] || p.foto)} alt={p.nama} className="w-full h-36 object-cover" />
              ) : (
                <div className="w-full h-36 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                  <ShoppingBag size={32} className="text-emerald-300" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-sm">{p.nama}</h3>
                    <p className="text-xs text-emerald-600 mt-0.5">{p.kategori}</p>
                    <p className="font-bold text-emerald-700 text-base mt-1.5">{rupiah(p.harga)}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => { setForm({ nama: p.nama, deskripsi: p.deskripsi, harga: p.harga.toString(), fotos: p.fotos?.length > 0 ? p.fotos : (p.foto ? [p.foto] : []), kategori: p.kategori, whatsapp: p.whatsapp||'', stok: p.stok?.toString()||'', is_active: p.is_active }); setEditId(p.id); setShowForm(true); }}
                      className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"><Pencil size={13} /></button>
                    <button onClick={() => handleDelete(p.id)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          {produk.length === 0 && <div className="col-span-3 text-center py-16 text-gray-400 text-sm">Belum ada produk</div>}
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4"
              onClick={e => e.target === e.currentTarget && setShowForm(false)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="font-playfair text-xl font-bold text-emerald-950">{editId ? 'Edit Produk' : 'Tambah Produk'}</h2>
                  <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X size={18} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {[['nama','Nama Produk *',true],['deskripsi','Deskripsi *',true]].map(([key,label,req]) => (
                    <div key={key}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
                      <input required={req} value={form[key]} onChange={e => setForm(f => ({...f, [key]: e.target.value}))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                  ))}
                  <MultiImageUpload
                    value={form.fotos}
                    onChange={v => setForm(f => ({ ...f, fotos: v }))}
                    maxPhotos={3}
                    label="Foto Produk"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Harga (IDR) *</label>
                      <input required type="number" value={form.harga} onChange={e => setForm(f => ({...f, harga: e.target.value}))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Kategori</label>
                      <select value={form.kategori} onChange={e => setForm(f => ({...f, kategori: e.target.value}))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20">
                        {['Buku','Seragam','Perlengkapan','Makanan','Lainnya'].map(k => <option key={k}>{k}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">WhatsApp Pemesanan (62xxx)</label>
                    <input value={form.whatsapp} onChange={e => setForm(f => ({...f, whatsapp: e.target.value}))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({...f, is_active: e.target.checked}))}
                      className="w-4 h-4 rounded accent-emerald-500" />
                    <span className="text-sm text-gray-700 font-medium">Tampilkan di toko</span>
                  </label>
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setShowForm(false)}
                      className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600">Batal</button>
                    <button data-testid="produk-form-submit" type="submit"
                      className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold shadow-emerald flex items-center justify-center gap-2">
                      <Check size={15} /> Simpan
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
